import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Switch, FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { tpaService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', isActive: true };

const TpaManagement = () => {
  const navigate = useNavigate();
  const [tpas, setTpas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await tpaService.getAll();
      setTpas(res.data);
    } catch {
      setError('Failed to load TPAs');
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, isActive: item.isActive });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await tpaService.update(editingItem.id, formData);
      } else {
        await tpaService.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch {
      setError('Failed to save TPA');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this TPA?')) return;
    try {
      await tpaService.delete(id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete TPA');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'TPA Name', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => (
        <Box sx={{
          px: 1.5, py: 0.3, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600,
          bgcolor: p.value ? 'success.light' : 'error.light',
          color: p.value ? 'success.dark' : 'error.dark',
        }}>
          {p.value ? 'Active' : 'Inactive'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="TPA Management" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add TPA
            </Button>
          </Box>
          <DataGrid
            rows={tpas}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit TPA' : 'Add TPA'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="TPA Name" value={formData.name} required autoFocus
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. MDIndia, Medi Assist"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">{editingItem ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default TpaManagement;
